import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, I, Btn } from "@/lib/bricks";
import { workspaceKind } from "@/lib/workspace";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Page, PageHeader, StatusTabs, List, Row, Empty, TextLink, Loading, FieldLabel, humanize } from "@/components/calm/Calm";

// Never imply licensed OREA/WEBForms. Legacy rows map to Custom; Office → Brokerage.
const catLabel = (cat) => {
  if (cat === 'Office') return 'Brokerage';
  if (cat === 'OREA' || cat === 'WEBForms') return 'Custom';
  return cat || 'Custom';
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}

const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'checkbox'];

// ─── Field builder dialog ────────────────────────────────────────────

function FieldBuilderDialog({ template, dark, p, session, BASE, onClose, onSaved }) {
  const [fields, setFields] = useState((template.fields || []).map(f => ({ ...f, optionsText: (f.options || []).join(', ') })));
  const [draft, setDraft] = useState({ label: '', type: 'text', required: true, optionsText: '', prefill: '' });
  const [saving, setSaving] = useState(false);
  const [prefillOptions, setPrefillOptions] = useState([]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  useEffect(() => {
    fetch(`${BASE}/api/forms/prefill-options`, { headers }).then(r => r.ok ? r.json() : []).then(d => setPrefillOptions(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line
  const prefillLabel = (k) => (prefillOptions.find(o => o.key === k) || {}).label || k;

  const addField = () => {
    if (!draft.label) return toast.error("Field label required");
    if (draft.type === 'select' && !draft.optionsText.trim()) return toast.error("Select fields need at least one option");
    setFields([...fields, { ...draft, id: `draft-${Date.now()}` }]);
    setDraft({ label: '', type: 'text', required: true, optionsText: '', prefill: '' });
  };
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = fields.map(f => ({
        label: f.label, type: f.type, required: !!f.required,
        options: f.type === 'select' ? f.optionsText.split(',').map(o => o.trim()).filter(Boolean) : null,
        prefill: f.prefill || null,
      }));
      const res = await fetch(`${BASE}/api/forms/templates/${template.id}`, { method: 'PATCH', headers, body: JSON.stringify({ fields: payload }) });
      if (!res.ok) throw new Error();
      toast.success("Fields saved");
      onSaved();
      onClose();
    } catch { toast.error("Failed to save fields"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Configure Fields — {template.name}</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Fields shown here are what agents fill out when they use this form.</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20 }}>
          {fields.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {fields.map((f, i) => (
                <div key={f.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 12 }}>
                  <span style={{ fontWeight: 500, flex: 1 }}>{f.label}</span>
                  <span style={{ color: p.ink3 }}>{humanize(f.type)}</span>
                  {f.prefill && <span style={{ color: p.ink3, fontSize: 11 }}>From deal: {prefillLabel(f.prefill)}</span>}
                  {f.required && <span style={{ color: p.ink3, fontSize: 12 }}>Required</span>}
                  <button onClick={() => removeField(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.ink3, padding: 2 }}><I name="x" size={12} color={p.ink3}/></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ padding: '14px 16px', border: `1px dashed ${p.hairS}`, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <input type="text" value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="Field label"
                style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none' }}/>
              <select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}
                style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {draft.type === 'select' && (
              <input type="text" value={draft.optionsText} onChange={e => setDraft({ ...draft, optionsText: e.target.value })} placeholder="Options, comma-separated"
                style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none' }}/>
            )}
            <select value={draft.prefill} onChange={e => setDraft({ ...draft, prefill: e.target.value })}
              style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
              <option value="">Don't fill from the deal</option>
              {prefillOptions.map(o => <option key={o.key} value={o.key}>Fill from deal: {o.label}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: p.ink3, cursor: 'pointer' }}>
              <input type="checkbox" checked={draft.required} onChange={e => setDraft({ ...draft, required: e.target.checked })}/>
              Required
            </label>
            <button onClick={addField} style={{ background: 'none', border: `1px solid ${p.hairS}`, borderRadius: 2, padding: '8px', cursor: 'pointer', color: p.ink2, fontFamily: F.display, fontSize: 12 }}>
              + Add Field
            </button>
          </div>
        </div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>
          <Btn variant="ghost" dark={dark} onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" dark={dark} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Fields'}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Fill-out dialog ─────────────────────────────────────────────────

function FillFormDialog({ template, dark, p, session, BASE, onClose, onSubmitted }) {
  const [values, setValues] = useState({});
  const [transactionId, setTransactionId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  useEffect(() => {
    fetch(`${BASE}/api/transactions`, { headers }).then(r => r.json()).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line

  const pickTransaction = async (id) => {
    setTransactionId(id);
    if (!id || !(template.fields || []).some(f => f.prefill)) return;
    try {
      const res = await fetch(`${BASE}/api/forms/prefill?template_id=${encodeURIComponent(template.id)}&transaction_id=${encodeURIComponent(id)}`, { headers });
      if (!res.ok) return;
      const { values: deal = {} } = await res.json();
      // Only fill fields the agent hasn't typed into yet.
      setValues(v => {
        const next = { ...v };
        Object.entries(deal).forEach(([k, val]) => { if (!next[k]) next[k] = val; });
        return next;
      });
      const n = Object.keys(deal).length;
      if (n) toast.success(`Filled ${n} field${n === 1 ? '' : 's'} from the deal`);
    } catch { /* agent can still type values */ }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/forms/submissions`, {
        method: 'POST', headers,
        body: JSON.stringify({ template_id: template.id, transaction_id: transactionId || null, data: values }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      const saved = await res.json().catch(() => ({}));
      if (transactionId && saved.deal_document) toast.success("Form submitted and filed as a PDF in the deal's documents");
      else if (transactionId) toast.warning("Form submitted, but the PDF couldn't be filed to the deal. Download it from Submissions.");
      else toast.success("Form submitted");
      onSubmitted();
      onClose();
    } catch (e) { toast.error(e.message || 'Failed to submit form'); }
    finally { setSubmitting(false); }
  };

  const fields = template.fields || [];
  const inputStyle = { marginTop: 6, width: '100%', padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 520, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>{template.name}</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{template.description || 'Fill out this form.'}</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20 }}>
          <div><FieldLabel>Link to a deal (optional)</FieldLabel>
            <select value={transactionId} onChange={e => pickTransaction(e.target.value)} style={{ ...inputStyle, background: p.bg }}>
              <option value="">No transaction</option>
              {transactions.map(t => <option key={t.id} value={t.id}>{t.property_address} — {t.deal_type}</option>)}
            </select>
          </div>
          {fields.length === 0 && (
            <div style={{ fontSize: 13, color: p.ink3 }}>This form has no fields configured yet.</div>
          )}
          {fields.map(f => (
            <div key={f.id}>
              <FieldLabel>{f.label}{f.required && <span style={{ color: p.ink3 }}> *</span>}</FieldLabel>
              {f.type === 'select' ? (
                <select value={values[f.id] || ''} onChange={e => setValues({ ...values, [f.id]: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                  <option value="">Select…</option>
                  {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea value={values[f.id] || ''} onChange={e => setValues({ ...values, [f.id]: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }}/>
              ) : f.type === 'checkbox' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color: p.ink2, cursor: 'pointer' }}>
                  <input type="checkbox" checked={values[f.id] === 'true'} onChange={e => setValues({ ...values, [f.id]: e.target.checked ? 'true' : 'false' })}/>
                  Yes
                </label>
              ) : (
                <input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} value={values[f.id] || ''}
                  onChange={e => setValues({ ...values, [f.id]: e.target.value })} style={inputStyle}/>
              )}
            </div>
          ))}
        </div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>
          <Btn variant="ghost" dark={dark} onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" dark={dark} onClick={handleSubmit} disabled={submitting || fields.length === 0}>{submitting ? 'Submitting…' : 'Submit Form'}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormsPage() {
  const { resolvedTheme } = useTheme();
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const kind = workspaceKind(user);
  const billingHref = kind === 'solo' ? '/solo/billing' : kind === 'team_lead' || kind === 'team_member' ? '/team/billing' : '/billing';
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [activeTab, setActiveTab] = useState('Templates');
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planGate, setPlanGate] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'Custom', description: '' });
  const [configuring, setConfiguring] = useState(null);
  const [filling, setFilling] = useState(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/forms/templates`, { headers })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (r.status === 403) {
          const detail = typeof d?.detail === 'string' ? d.detail : 'Forms require the Marble plan or higher. Upgrade in Billing to unlock.';
          setPlanGate(detail);
          setTemplates([]);
          setSubmissions([]);
        } else {
          setPlanGate(null);
          setTemplates(Array.isArray(d) ? d : []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch(`${BASE}/api/forms/submissions`, { headers })
      .then(async (r) => {
        if (r.status === 403) return;
        const d = await r.json().catch(() => null);
        setSubmissions(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!form.name) return toast.error("Form name required");
    try {
      const res = await fetch(`${BASE}/api/forms/templates`, { method: 'POST', headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      toast.success("Form template created");
      setShowNew(false);
      setForm({ name: '', category: 'Custom', description: '' });
      load();
    } catch { toast.error("Failed to create template"); }
  };

  const openPdf = async (sub) => {
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`${BASE}/api/forms/submissions/${sub.id}/pdf`, { headers: { Authorization: `Bearer ${session?.access_token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.location.href = url;
    } catch {
      if (win) win.close();
      toast.error("Couldn't open the PDF");
    }
  };

  const TABS = ['Templates', 'Submissions'];

  if (loading) return <Loading />;

  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: p.ink2, fontFamily: F.display, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS };

  return (
    <Page testid="forms-page" width={960}>
      <PageHeader title="Forms"
        subtitle={planGate ? 'Custom templates and submissions come with Marble and above' : `${templates.length} templates, ${submissions.length} submissions`}
        action={!planGate && activeTab === 'Templates' ? <Btn variant="primary" dark={dark} onClick={() => setShowNew(true)}>New template</Btn> : null} />

      {planGate && (
        <Empty testid="forms-plan-gate" action={<Btn variant="accent" dark={dark} onClick={() => navigate(billingHref)}>Open billing</Btn>}>
          <div style={{ fontSize: 16, fontWeight: 500, color: p.ink, marginBottom: 6 }}>Forms need Marble</div>
          {planGate}
        </Empty>
      )}

      {!planGate && (<>
      <StatusTabs label="View" options={TABS.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} testid="forms-tabs" />

      {activeTab === 'Templates' && (
        templates.length === 0 ? (
          <Empty action={<TextLink onClick={() => setShowNew(true)}>Create your first template</TextLink>}>No form templates yet.</Empty>
        ) : (
          <List testid="forms-templates">
            {templates.map(t => (
              <Row key={t.id} testid={`template-${t.id}`}
                title={t.name}
                meta={[catLabel(t.category), { text: `${t.field_count || 0} fields`, figure: false }]}
                next={t.description || null}
                aside={<>
                  <button onClick={() => setConfiguring(t)} style={linkBtn}>Configure</button>
                  <button disabled={!t.field_count} onClick={() => setFilling(t)} title={t.field_count ? undefined : 'Add fields first'}
                    style={{ ...linkBtn, color: p.ink, cursor: t.field_count ? 'pointer' : 'not-allowed', opacity: t.field_count ? 1 : 0.45 }}>Use form</button>
                </>} />
            ))}
          </List>
        )
      )}
      {configuring && (
        <FieldBuilderDialog template={configuring} dark={dark} p={p} session={session} BASE={BASE}
          onClose={() => setConfiguring(null)} onSaved={load}/>
      )}
      {filling && (
        <FillFormDialog template={filling} dark={dark} p={p} session={session} BASE={BASE}
          onClose={() => setFilling(null)} onSubmitted={load}/>
      )}

      {activeTab === 'Submissions' && (
        submissions.length === 0 ? <Empty>No form submissions yet.</Empty> : (
          <List testid="forms-submissions">
            {submissions.map(sub => (
              <Row key={sub.id} testid={`submission-${sub.id}`}
                title={sub.form_name}
                meta={[sub.agent_name, sub.transaction_address, { text: fmtDate(sub.created_at), figure: true }]}
                status={humanize(sub.status || 'submitted')}
                attention={sub.status === 'rejected'}
                aside={<button onClick={() => openPdf(sub)} style={linkBtn}>PDF</button>} />
            ))}
          </List>
        )
      )}

      {/* New Template Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>New form template</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Create a reusable form template for your brokerage.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><FieldLabel>Template name</FieldLabel>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Listing intake"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div><FieldLabel>Category</FieldLabel>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {['Brokerage','Custom'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div><FieldLabel>Description</FieldLabel>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="What this form is for"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreate}>Create template</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </Page>
  );
}
